<script lang="ts" setup>
//#region imports
import { ref, computed, watch } from 'vue';
import { useTypedStore } from '@/store';
import { useTranslateLanguages } from '@/composables/useTranslateLanguages';
import { useError } from '@/composables/useError';
import { useApi } from '@/api';
import LanguagesSetup from '@/components/UIElements/LanguagesSetup.vue';
import type { EmitsToListeners, TableMethods } from '@/types/core';
import type { GetAffiliatesData, GetAffiliatesResponse, PostAffiliateNotificationsData } from '@/types/openapi-ts';
//#endregion

//#region types
interface Emits {
  'close': [],
  'success': [],
}

export interface SendNotificationPopupParams {
  listeners?: EmitsToListeners<Emits>;
}
//#endregion

//#region define
const emit = defineEmits<Emits>();
//#endregion

//#region composables
const api = useApi();
const store = useTypedStore();
const { selectedLanguages, defaultLanguageIndex, languages, index, handleSelectTab } = useTranslateLanguages({
  actions: computed(() => {
    return notificationParams.value || [];
  }),
});
const { getError } = useError({
  errors: computed(() => {
    return errors.value || [];
  }),
});
//#endregion

//#region state
const isVisible = ref(false);
const isLoading = ref(false);
const errors = ref<any>({});

const tableRef = ref<TableMethods | null>(null);
const isRequiredRead = ref(false);
const notificationParams = ref([
  {
    title: '',
    message: '',
    language_code: 'en',
    is_default: true,
  },
]);

const filter = ref('all');
const isDataLoading = ref(false);
const searchQuery = ref('');
const page = ref(1);
const limit = ref(14);
const count = ref(0);
const isFirstLoading = ref(true);
const affiliatesCount = ref(0);
const selectedIds = ref<string[]>([]);
const tableData = ref<GetAffiliatesResponse['payload']>([]);
//#endregion

//#region computed
const customerLanguages = computed(() => store.getters['misc/customerLanguages']);

const selectedMap = computed(() => {
  return selectedIds.value.reduce((acc, id) => {
    acc[id] = true;
    return acc;
  }, {} as Record<string, boolean>);
});

const countNotification = computed(() => {
  if (filter.value === 'only') {
    return selectedIds.value.length;
  }

  if (filter.value === 'except') {
    return affiliatesCount.value - selectedIds.value.length;
  }

  return affiliatesCount.value;
});
//#endregion

//#region watch
watch(filter, () => {
  if (tableRef.value) {
    tableRef.value.clearCheck();
  }
});
//#endregion

//#region methods
function open() {
  fetchData();
  isVisible.value = true;
}

function handleSave() {
  const [main, ...restActions] = notificationParams.value;
  const query = {
    title: main?.title,
    message: main?.message,
    filter: filter.value,
    is_required_to_read: isRequiredRead.value,
    affiliate_ids: selectedIds.value,
    translations: restActions,
  } as PostAffiliateNotificationsData['body'];

  api.postAffiliatesNotifications(query).then(() => {
    emit('success');
    handleClose();
  }).catch((e) => {
    const { title, message, translations = [] } = e.data.errors || {};
    const mainError = title || message ? { title, message } : null;

    errors.value = {
      ...e.data.errors,
      translateErrors: [mainError, ...translations],
    };
  });
}

function handleClose() {
  emit('close');
  isVisible.value = false;
  errors.value = {};
}

function _query() {
  return {
    search: searchQuery.value,
    limit: limit.value,
    offset: limit.value * page.value - limit.value,
    sort_column: 'email',
    sort_dir: 'asc',
    account_status: 'approved',
    email_verified: true,
  } as GetAffiliatesData['query'];
};

function fetchData() {
  if (!isFirstLoading.value) {
    isDataLoading.value = true;
  }

  const options = _query();

  api.getAffiliates(options).then((res) => {
    if (searchQuery.value === '') {
      affiliatesCount.value = res.data.misc!.count;
    }

    count.value = res.data.misc!.count;
    tableData.value = res.data.payload;
  }).finally(() => {
    if (!isFirstLoading.value) {
      isDataLoading.value = false;
    } else {
      isFirstLoading.value = false;
    }
  });
}

function handleAddLanguage(languageCode: string) {
  notificationParams.value.push({
    title: '',
    message: '',
    is_default: false,
    language_code: languageCode,
  });
  index.value = notificationParams.value.length - 1;
}

function handleDelete(id: number) {
  index.value = defaultLanguageIndex.value;
  notificationParams.value.splice(id, 1);

  if (errors.value.translateErrors) {
    errors.value.translateErrors.splice(id, 1);
  }
}

function handleRefresh() {
  page.value = 1;
  fetchData();
}

function pageChange(newPage: number, newSize: number) {
  if (page.value !== newPage || limit.value !== newSize) {
    page.value = newPage;
    limit.value = newSize;
    fetchData();
  }
}

function checkedRows(id: string, ids: string[]) {
  selectedIds.value = ids;
}

//#endregion

defineExpose({
  open,
});
</script>

<template>
  <Popup
    v-model:is-visible="isVisible"
    :is-loading="isLoading"
    :title="$t('affiliates.notifications.popup.sendNotification')"
    :width="1140"
    class="send-notification-popup"
    @close="handleClose"
  >
    <template v-slot:body>
      <div class="send-notification-popup__body">
        <div class="send-notification-popup__block t:space-y-16">
          <div class="t:px-24 t:py-16 t:space-y-16">
            <UiLabel
              :font-size="16"
              :label="$t('affiliates.notifications.popup.notificationDetails')"
              bold
            />
            <CardTitle
              :title="$t('affiliates.notifications.popup.requiredRead')"
            >
              <UiSwitch
                v-model="isRequiredRead"
              />
            </CardTitle>
          </div>
          <LanguagesSetup
            :selected-index="index"
            :languages="languages"
            :selected-languages="selectedLanguages"
            :can-make-default="false"
            :errors="errors.translateErrors"
            class="t:grow"
            @add-language="handleAddLanguage"
            @select-index="handleSelectTab"
            @delete-language="handleDelete(index)"
          >
            <div class="t:space-y-16">
              <UiInput
                v-model="notificationParams[index]!.title"
                :error="getError(['translateErrors', index, 'title'])"
                :label="$t('affiliates.notifications.popup.title')"
                :placeholder="$t('affiliates.notifications.popup.title')"
                autosize
                size="big"
              />
              <UiTextarea
                v-model="notificationParams[index]!.message"
                :error="getError(['translateErrors', index, 'message'])"
                :label="$t('affiliates.notifications.popup.description')"
                :placeholder="$t('affiliates.notifications.popup.description')"
                :rows="9"
              />
            </div>
          </LanguagesSetup>
        </div>
        <div class="send-notification-popup__block t:space-y-16 t:px-24 t:py-16">
          <UiLabel
            :font-size="16"
            :label="$t('affiliates.notifications.affiliates')"
            bold
          />
          <UiRadioGroup
            v-model="filter"
            :options="[
              {
                label: $t('affiliates.notifications.popup.all'),
                value: 'all',
              },
              {
                label: $t('affiliates.notifications.popup.onlySelected'),
                value: 'only',
              },
              {
                label: $t('affiliates.notifications.popup.allExcept'),
                value: 'except',
              }
            ]"
          />
          <div class="t:flex">
            <UiInput
              v-model.trim="searchQuery"
              class="t:mr-8"
              is-search
              width="100%"
              @search="handleRefresh"
            />
            <UiPagination
              :count="count"
              :page="page"
              :page-size="limit"
              @page-change="pageChange"
            />
          </div>
          <UiTableNew
            ref="tableRef"
            v-loading="isDataLoading"
            :column-checkbox-width="34"
            :data="tableData"
            :external-height="512"
            :select-row="filter !== 'all'"
            :show-check-all="false"
            checked-row-id="id"
            class="send-notification-popup__table"
            element-loading-custom-class="data-loader-spinner"
            is-static
            lazy-loading
            @checked-rows="checkedRows"
          >
            <template v-slot:append>
              <ElTableColumn>
                <template v-slot:header>
                  E-Mail
                </template>
                <template v-slot="scope">
                  <UiCheckbox
                    :disabled="filter === 'all'"
                    :label="scope.row.email"
                    :model-value="filter === 'all' || selectedMap[scope.row.id]"
                  />
                </template>
              </ElTableColumn>
            </template>
          </UiTableNew>
        </div>
      </div>
    </template>
    <template v-slot:footer>
      <div class="t:justify-end t:flex t:space-x-10">
        <UiButton
          :disabled="isLoading"
          icon="times"
          size="big"
          type="negative"
          @click="handleClose"
        >
          {{ $t('crm.buttons.cancel') }}
        </UiButton>
        <UiButton
          :disabled="isLoading || (filter !== 'all' && !selectedIds.length)"
          filled
          icon="check"
          size="big"
          @click="handleSave"
        >
          {{ $t('affiliates.notifications.popup.confirmSend', { count: countNotification || '' }, countNotification) }}
        </UiButton>
      </div>
    </template>
  </Popup>
</template>

<style lang="scss">
.send-notification-popup {
  &__body {
    height: 660px;
    display: flex;
    margin: -16px -24px;
  }

  &__table {
    padding-bottom: 0 !important;
  }

  &__block {
    width: 40%;
    display: flex;
    flex-direction: column;
    &:first-child {
      width: 60%;
      border-right: 1px solid var(--border-default);
    }
  }
}
</style>
